import React, {useEffect, useState, useContext, useMemo, useRef} from 'react'
import moment from 'moment'
import _, {update} from 'lodash'
import './ReservationGantt.scss'
import SearchIcon from '@material-ui/icons/Search'
import {
  TextField,
  InputAdornment,
  IconButton,
  Modal,
  Popover,
  Button,
  Tooltip,
} from '@material-ui/core'
import ResourceRow from './ItemRow'
import ResourceGroup from './ItemGroup'
import EventRow from './EventRow'
import EventGroup from './EventGroup'
import DateRangeSelector from './DateRangeSelector'
import DateTimeIndicator from './DateTimeIndicator'
import KeyboardArrowLeftRoundedIcon from '@material-ui/icons/KeyboardArrowLeftRounded'
import KeyboardArrowRightRoundedIcon from '@material-ui/icons/KeyboardArrowRightRounded'
import FilterListIcon from '@material-ui/icons/FilterList'
import {OverrideViewBreakpoints} from './ViewBreakPoints'
import PrimaryHeaders from './PrimaryHeaders'
import SecondaryHeaders from './SecondaryHeaders'
import ItemList from './ItemList'
import PillRows from './PillRows'
import useReservationGanttContext from '../../../context/ReservationGantt/ReservationGantt'
import {UserContext} from '../../../context/User'
import {ReservationContext} from '../../../context/Reservation'
import GanttFilters from './GanttFilters'
import useCalendarViewContext from '../../../context/CalendarView'
import ReservationReserve from '../../ReservationReserve/ReservationReserve'
import ReservedForOptions from '../../ReservationReserve/CustomComponents/ReservedForOptions'
import ReservedForGantt from './ReservedForGantt'
import FieldFilter from '../../ReservationReserve/FieldFilter'
import PopFieldToggle from '../../ReservationReserve/FieldFilter/PopFieldToggle'
import {ReservationCalendarMainContext} from '../../../context/ReservationCalendarMain'
import useGanttItemLoader from './useGanttItemLoader'
import GanttGroup from './GanttGroup/GanttGroup'
import DateRange from './DateRange/DateRange'
import GanttGroupReserved from './GanttGroup/GanttGroupReserved'
import {getFilterFieldValues} from '../../../state/entities/get-filter-field-values/actions'

window.moment = moment

const RootClass = 'ap-reservation-gantt'

const ReservationGantt = ({
  currentDate,
  viewSecLength,
  onClickHead = (startSec, secInterval) => {},
}) => {
  const refTimeLineHeader = React.useRef(null)
  const refRightSection = React.useRef(null)
  const refMainGroup = React.useRef(null)
  const refGanttBodyWrapper = React.useRef(null)
  // contexts
  const {
    setViewData,
    setFilters,
    filters,
    startDateSec,
    setStartDateSec,
    viewBreakPoint,
    setViewBreakPoint,
    columnHeaders,
    setColumnHeaders,
    groupByItems,
    groupBy,
    query,
    setQuery,
    loadMoreGroupItems,
    groupByItemsDispatch,
  } = useReservationGanttContext()
  const [windowWResize, setWindowWResize] = useState(document.documentElement.clientWidth)
  const {viewData, selectedViewGroup, shareableViewData} = useCalendarViewContext()

  const _viewData = useMemo(() => {
    if (!_.isEmpty(shareableViewData?.selectedView?.group_view)) return shareableViewData
    return viewData
  }, [viewData, shareableViewData])

  const {account_time_format} = useContext(UserContext)
  const {
    update,
    reservation,
    entityObjects,
    entityFields,
    configuration,
    loadConfiguration,
    setOpenReserveForPopOver,
    openReserveForPopOver,
  } = useContext(ReservationContext)
  const {setSliderLength, selectedDate, setSelectedDate} = useContext(
    ReservationCalendarMainContext
  )
  const [filterAnchor, setFilterAnchor] = useState(null)
  // const {resources, loadItems} = useGanttItemLoader()
  const {resources, setLimit, loadItems, setAdditionalFilters} = useGanttItemLoader()

  const ViewBreakPoints = useMemo(() => {
    return account_time_format === '%R' || account_time_format === '%T'
      ? OverrideViewBreakpoints({hour: {primaryFormat: {label: 'HH', label_sub: null}}})
      : OverrideViewBreakpoints()
  }, [account_time_format])

  const handleContinue = id => {
    update('selected_group', {
      id: selectedViewGroup.id,
      name: selectedViewGroup.name,
      key: selectedViewGroup.key,
    })
    setOpenReserveForPopOver(null)
  }

  const handleCloseReserveForPopup = e => {
    setOpenReserveForPopOver(null)
    handleClose(e)
    update('reserved_for', {})
    update('selected_items', [])
  }

  const handleClose = e => {
    update('isSelection', false)
    update('date_from', null)
    update('date_to', null)
    e.stopPropagation()
  }
  useEffect(() => {
    // get breakpoint
    const breakpoint = ViewBreakPoints.find(
      bp => viewSecLength >= bp.percentMin && viewSecLength <= bp.percentMax
    )

    // get width based on percentage of zoom level vs min-max of breakpoint width
    const percRange = breakpoint.percentMax - breakpoint.percentMin
    const percZoom = viewSecLength - breakpoint.percentMin
    const perc = percZoom / percRange
    const widthRange = breakpoint.maxWidth - breakpoint.minWidth
    const width = breakpoint.minWidth + perc * widthRange

    const rect = refRightSection.current.getBoundingClientRect()
    // get max count
    const maxColumnCount = Math.ceil(rect.width / width)

    // create headers
    let primaryHeads = []
    let secondaryHeads = []
    let currentMS = breakpoint.getMoment(breakpoint.getPrevMoment(startDateSec, 1))
    let prevMS = breakpoint.getMoment(breakpoint.getPrevMoment(currentMS.valueOf()))
    let i = 0
    let prevSecondaryKey = null
    do {
      const hMoment = breakpoint.getNextMoment(prevMS.valueOf()).locale('en-short')
      const ph = {
        current_day: breakpoint.getMoment(currentDate.valueOf()).valueOf() === hMoment.valueOf(),
        width: width,
        endSec: breakpoint
          .getNextMoment(hMoment.valueOf())
          .subtract({second: 1})
          .valueOf(),
        sec: hMoment.valueOf(),
        label: hMoment.format(breakpoint.primaryFormat.label),
        label_sub: breakpoint.primaryFormat.label_sub
          ? hMoment.format(breakpoint.primaryFormat.label_sub)
          : null,
      }

      const sh = {
        width: width,
        sec: hMoment.valueOf(),
        label: hMoment.format(breakpoint.secondaryFormat.label),
        label_sub: breakpoint.secondaryFormat.label_sub
          ? hMoment.format(breakpoint.secondaryFormat.label_sub)
          : null,
        colSpan: 1,
      }
      const skey = `${sh.label} ${sh.label_sub}`
      if (skey !== prevSecondaryKey) {
        secondaryHeads.push(sh)
        prevSecondaryKey = skey
      } else {
        secondaryHeads[secondaryHeads.length - 1].colSpan++
      }

      primaryHeads.push(ph)
      prevMS = hMoment
    } while (i++ < maxColumnCount)

    setColumnHeaders({
      primary: primaryHeads,
      secondary: secondaryHeads,
    })

    setViewBreakPoint(breakpoint)
  }, [
    refRightSection,
    setColumnHeaders,
    startDateSec,
    refTimeLineHeader,
    currentDate,
    ViewBreakPoints,
    setViewBreakPoint,
    viewSecLength,
    windowWResize,
  ])

  // fix scroll width
  useEffect(() => {
    if (columnHeaders.primary.length > 0) {
      const leftOffset = columnHeaders.primary[0].width - 26
      if (refTimeLineHeader.current) {
        refTimeLineHeader.current.scrollLeft = leftOffset
      }
    }
  }, [columnHeaders, windowWResize, refTimeLineHeader])

  useEffect(() => {
    if (
      _viewData.selectedView?.group_view?.entity_id
      //  && viewData.selectedView?.group_view?.entity_id !== v.selectedView?.group_view?.entity_id
    ) {
      loadConfiguration(_viewData.selectedView?.group_view?.entity_id)
    }
  }, [_viewData, loadConfiguration])

  useEffect(() => {
    setViewData(_viewData)
  }, [_viewData, setViewData])

  useEffect(() => {
    const f = () => {
      setWindowWResize(document.documentElement.clientWidth)
    }
    setWindowWResize(document.documentElement.clientWidth)
    window.addEventListener('resize', f)
    return () => {
      window.removeEventListener('resize', f)
    }
  }, [])

  const handleVScroll = e => {
    // lazy load here
    if (
      Math.round(e.currentTarget.offsetHeight + e.currentTarget.scrollTop) >=
        Math.round(e.currentTarget.scrollHeight) &&
      groupByItems.data.length === 0
    ) {
      // request here
      if (refMainGroup.current) {
        refMainGroup.current.loadMore()
      } else {
        loadMoreGroupItems()
      }
    }
  }
  const handleScrollFix = e => {
    const width = columnHeaders?.primary[0]?.width || 0
    if (!!width) {
      const leftOffset = width - 26
      refTimeLineHeader.current.scrollLeft = leftOffset
      // refTimelineEvents.current.scrollLeft = leftOffset
    }
  }

  const handleNext = (startMilliSec, viewBreakpoint) => {
    setStartDateSec(viewBreakpoint.getNextMoment(startMilliSec))
  }
  const handlePrev = (startMilliSec, viewBreakpoint) => {
    setStartDateSec(viewBreakpoint.getPrevMoment(startMilliSec))
  }
  const handleClickHead = (startMilliSec, breakpoint) => {
    setSliderLength(breakpoint.percentMin)
    setStartDateSec(breakpoint.getMoment(startMilliSec))
  }

  const handleClosePopup = (e, filters) => {
    setFilterAnchor(null)
    setFilters([...filters])
  }

  const staticViewBreakPoint = useRef(viewBreakPoint)
  staticViewBreakPoint.current = viewBreakPoint

  useEffect(() => {
    if (staticViewBreakPoint.current.index === 2) {
      setSliderLength(40)
    }
    setStartDateSec(selectedDate.valueOf())
  }, [selectedDate, setStartDateSec, ViewBreakPoints, setSliderLength])

  const handleLoadMore = () => {
    if (groupBy) {
      getFilterFieldValues(groupByItemsDispatch, groupBy.field_id, null, {
        limit: groupByItems.limit,
        page: groupByItems.page + 1,
      })
    }
  }

  const allLoaded = groupBy?.values.length < groupByItems.page * groupByItems.limit

  return (
    <div className="ap-reservation-gantt">
      {/* left section*/}
      <div className={`c-gantt__section c-gantt__section--left`}>
        <div className="c-gantt__header">
          <Tooltip title={selectedViewGroup?.name}>
            <div className="c-gantt__title ap-typography ap-typography--lg view-head-name">
              {selectedViewGroup?.name}
            </div>
          </Tooltip>
          <GanttFilters
            filterAnchor={filterAnchor}
            filters={filters}
            setFilters={setFilters}
            setFilterAnchor={setFilterAnchor}
            setQuery={setQuery}
            query={query}
          />
        </div>
      </div>
      {/* right section*/}
      <div ref={refRightSection} className={`c-gantt__section c-gantt__section--right`}>
        {/* floating-buttons */}
        <div className={`${RootClass}__btn-prev-step`}>
          <IconButton
            className="ap-icon-button"
            onClick={e => handlePrev(viewBreakPoint.getMoment(startDateSec), viewBreakPoint)}
            size="small"
          >
            <KeyboardArrowLeftRoundedIcon className="ap-reservation-gantt__next-prev-arrow" />
          </IconButton>
        </div>
        <div className={`${RootClass}__btn-next-step`}>
          <IconButton
            className="ap-icon-button"
            onClick={e => handleNext(viewBreakPoint.getMoment(startDateSec), viewBreakPoint)}
            size="small"
          >
            <KeyboardArrowRightRoundedIcon className="ap-reservation-gantt__next-prev-arrow" />
          </IconButton>
        </div>

        {/* headers */}
        <div ref={refTimeLineHeader} className="c-gantt__header" onScrollCapture={handleScrollFix}>
          <div className={`${RootClass}__date-container`}>
            {/* secondary */}
            <SecondaryHeaders
              headers={columnHeaders.secondary}
              // onClick={(ev, h) =>
              //   handleClickHead(h.sec, ViewBreakPoints[viewBreakPoint.zoomOutBreakPointIndex])
              // }
            />
            {/* primary */}
            <PrimaryHeaders
              headers={columnHeaders.primary}
              style={{
                pointerEvents: !viewBreakPoint?.index ? 'none' : 'all',
              }}
              onClick={(ev, h) =>
                handleClickHead(h.sec, ViewBreakPoints[viewBreakPoint.zoomInBreakPointIndex])
              }
            />
          </div>
        </div>
      </div>
      {/* Body */}
      <div className="c-gantt__section--body c-gantt__body ap-scrollbar" onScroll={handleVScroll}>
        <div ref={refGanttBodyWrapper} className="c-gantt__body-wrapper" data-x="date-selection">
          <DateRange
            parentEl={refGanttBodyWrapper}
            columnHeaders={columnHeaders}
            currentDate={currentDate}
          />
          <DateTimeIndicator primaryHeaders={columnHeaders.primary} startSec={moment().valueOf()} />
          {!!reservation.fromGantt && reservation.selected_items.length > 0 && (
            <GanttGroupReserved
              title={`For ${reservation?.reserved_for?.name}`}
              ref={refMainGroup}
              columnHeaders={columnHeaders}
              viewBreakPoint={viewBreakPoint}
            />
          )}
          {_.isEmpty(_viewData.selectedView) && (
            <div
              style={{
                zIndex: 9999,
                background: '#fff',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              <div style={{textAlign: 'center', marginTop: 120}}>
                Select a view to see Gantt chart
              </div>
            </div>
          )}
          {!_.isEmpty(_viewData.selectedView) && groupByItems.data.length === 0 && (
            <GanttGroup
              index={-1}
              key={`gantt-group_${_viewData.selectedView.group_view.entity_id}_${_viewData.selectedFilteredViews?.length}`}
              title={
                reservation.selected_items.length === 0
                  ? null
                  : _viewData.selectedFilteredViews.length === 1
                  ? _viewData.selectedFilteredViews[0]?.name
                  : selectedViewGroup?.name
              }
              ref={refMainGroup}
              columnHeaders={columnHeaders}
              viewBreakPoint={viewBreakPoint}
            />
          )}
          {!_.isEmpty(_viewData.selectedView) && groupBy && groupByItems.data.length > 0 && (
            <GanttGroup
              index={-1}
              key={`__no_${_viewData.selectedView.group_view.entity_id}_${_viewData.selectedFilteredViews?.length}_${groupBy.field_name}`}
              ref={refMainGroup}
              columnHeaders={columnHeaders}
              viewBreakPoint={viewBreakPoint}
              groupBy={groupBy}
              groupByFilter={[null, {$oid: ''}, null]}
              title={`No ${groupBy.field_name}`}
              filterValue={''}
              applied_filters={{[groupBy.key]: ['No value']}}
            />
          )}
          <div>
            {groupBy &&
              groupByItems.data.map((g, k) => (
                <GanttGroup
                  index={k}
                  key={g[1]['$oid']}
                  title={`${g[0]} - ${g[2]['data-secondary_value'] || ''}`}
                  groupBy={groupBy}
                  columnHeaders={columnHeaders}
                  viewBreakPoint={viewBreakPoint}
                  filterValue={g[1]['$oid']}
                  filterData={
                    g[0] !== ' ' && !_.isEmpty(g[0]) ? g[0] : g[2]['data-secondary_value']
                  }
                />
              ))}
            {groupBy && !allLoaded && groupByItems.data.length > 0 && (
              <div
                style={{paddingLeft: '20px', cursor: 'pointer', marginBottom: '10px'}}
                className="ap-typography ap-typography--sm ap-typography--nowrap c-gantt-group__btn-load-more"
                onClick={handleLoadMore}
              >
                Load More
              </div>
            )}

            {groupBy && allLoaded && (
              <div
                style={{paddingLeft: '20px', marginBottom: '10px'}}
                className="ap-typography ap-typography--sm ap-typography--nowrap c-gantt-group__btn-load-more"
              >
                No more items
              </div>
            )}
          </div>

          <PopFieldToggle
            open={!!filterAnchor}
            anchorEl={filterAnchor}
            onClose={handleClosePopup}
            options={entityFields}
            objects={entityObjects}
            filters={filters}
          />

          <Popover
            open={Boolean(openReserveForPopOver)}
            anchorReference="anchorPosition"
            anchorPosition={openReserveForPopOver?.anchorPosition}
            onClose={handleCloseReserveForPopup}
            className="c-res-mod popup-z-index"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            PaperProps={{
              style: {overflow: 'visible'},
            }}
          >
            <ReservedForGantt
              handleClose={handleCloseReserveForPopup}
              handleContinue={handleContinue}
            />
          </Popover>
        </div>
      </div>
    </div>
  )
}

export default ReservationGantt
